import React, { useEffect, useState } from "react";
import { spotlight, featuredWomen, generalPool } from "./thoughtLeaders";

type Person = {
  name: string;
  role: string;
  avatar: string;
};

const Instructors = () => {
  const [records, setRecords] = useState<Person[]>([]);
  const [fadingPositions, setFadingPositions] = useState<number[]>([]);

  const pickRandom = <T,>(arr: T[], count: number): T[] =>
    [...arr].sort(() => Math.random() - 0.5).slice(0, count);

  const getSevenRecords = (
    showFirst: Person[],
    females: Person[],
    rest: Person[]
  ): Person[] => [
    ...pickRandom(showFirst, 2),
    ...pickRandom(females, 2),
    ...pickRandom(rest, 3),
  ];

  const generateReplacementPositions = (length: number = 7): [number, number] => {
    let a = Math.floor(Math.random() * length);
    let b = Math.floor(Math.random() * length);
    while (b === a) b = Math.floor(Math.random() * length);
    return [a, b];
  };

  const getPoolForIndex = (index: number): Person[] => {
    if (index < 2) return spotlight;
    if (index < 4) return featuredWomen;
    return generalPool;
  };

  const replacements = (
    prev: Person[],
    positions: [number, number]
  ): Person[] => {
    const next = [...prev];
    const names = next.map((r) => r.name);

    positions.forEach((index) => {
      const pool = getPoolForIndex(index).filter(
        (record) => !names.includes(record.name)
      );
      const replacement = pool[Math.floor(Math.random() * pool.length)];

      if (replacement) {
        next[index] = replacement;
        names[index] = replacement.name; // Update the names array to avoid duplicates in the same rotation
      }
    });
    return next;
  };

  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // initial load
    const initialRecords = getSevenRecords(
      spotlight,
      featuredWomen,
      generalPool
    );
    setRecords(initialRecords);
    
    if (reduced) return;

    const rotate = () => {
      const positions = generateReplacementPositions();
      setFadingPositions(positions);

      // Queue record replacement after fade-out completes
      setTimeout(() => {
        setRecords((prev) => {
          const next = replacements(prev, positions);
          return next;
        });

        // remove fade after swap
        setTimeout(() => {
          setFadingPositions([]);
        }, 400);
      }, 400);
    };

    const interval = setInterval(rotate, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="instructor-strip">
      <div className="inner">
        <div className="label">Learn from the best in the business</div>

        <div className="instructor-row">
          {records.map((record, index) => (
            <div
              key={record.name + index}
              className={`instructor ${
                fadingPositions.includes(index) ? "fading" : ""
              }`}
            >
              <div className="avatar">
                <img
                  src={record.avatar}
                  alt={record.name}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="name">{record.name}</div>
              <div className="role">{record.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Instructors;
